import React, { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { Plus, Search, Edit2, Trash2, Users, UserCheck, Key, Upload, Mail } from 'lucide-react';
import { formatPhone, parsePhone } from '../utils/masks';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { BulkUserImport } from './BulkUserImport';
import { TransferPropertiesModal } from './TransferPropertiesModal';

interface Profile {
  id: string;
  user_id?: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  role: 'corretor' | 'gerente' | 'rh' | 'diretor';
  manager_id?: string | null;
  manager?: {
    first_name: string;
    last_name: string;
  } | null;
  is_active: boolean;
  force_password_change: boolean;
  created_at: string;
  creci?: string | null;
  secondary_email?: string | null;
}

interface ManagerOption {
  id: string;
  first_name: string;
  last_name: string;
}

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<Profile[]>([]);
  const [managers, setManagers] = useState<ManagerOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [editingUser, setEditingUser] = useState<Profile | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<Profile | null>(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    secondary_email: '',
    phone: '',
    role: 'corretor' as Profile['role'],
    manager_id: '',
    creci: ''
  });
  
  const [displayPhone, setDisplayPhone] = useState('');

  useEffect(() => {
    loadUsers();
    loadManagers();
  }, []);

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          manager:manager_id (
            first_name,
            last_name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadManagers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('role', ['gerente', 'diretor'])
        .eq('is_active', true)
        .order('first_name');

      if (error) throw error;
      setManagers(data || []);
    } catch (error) {
      console.error('Erro ao carregar gerentes:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingUser) {
        // Atualizar usuário existente
        const { error } = await supabase
          .from('profiles')
          .update({
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            secondary_email: formData.secondary_email?.trim() || null,
            phone: parsePhone(formData.phone) || null,
            role: formData.role,
            manager_id: formData.role === 'corretor' ? formData.manager_id : null,
            creci: (['corretor', 'gerente', 'diretor'].includes(formData.role)) ? formData.creci || null : null
          })
          .eq('id', editingUser.id);

        if (error) throw error;
      } else {
        // Criar novo usuário
        // Primeiro criar o usuário no auth
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: formData.email,
          password: 'Fox1234!',
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: {
              first_name: formData.first_name,
              last_name: formData.last_name
            }
          }
        });

        if (authError) throw authError;

        // Criar o perfil
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            user_id: authData.user?.id,
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            secondary_email: formData.secondary_email?.trim() || null,
            phone: parsePhone(formData.phone) || null,
            role: formData.role,
            manager_id: formData.role === 'corretor' ? formData.manager_id : null,
            force_password_change: true,
            creci: (['corretor', 'gerente', 'diretor'].includes(formData.role)) ? formData.creci || null : null
          });

        if (profileError) throw profileError;

        // Enviar email de boas-vindas
        try {
          const { error: emailError } = await supabase.functions.invoke('send-welcome-email', {
            body: {
              firstName: formData.first_name,
              lastName: formData.last_name,
              email: formData.email,
              role: formData.role,
              isFirstLogin: true
            }
          });

          if (emailError) {
            console.error('Erro ao enviar email de boas-vindas:', emailError);
            // Não bloqueia o cadastro se o email falhar
          } else {
            console.log('Email de boas-vindas enviado com sucesso!');
          }
        } catch (emailError) {
          console.error('Erro ao enviar email de boas-vindas:', emailError);
          // Não bloqueia o cadastro se o email falhar
        }
      }

      setShowForm(false);
      setEditingUser(null);
      setDisplayPhone('');
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        secondary_email: '',
        phone: '',
        role: 'corretor',
        manager_id: '',
        creci: ''
      });
      
      await loadUsers();
      await loadManagers();

      toast({
        title: "Sucesso!",
        description: editingUser ? "Usuário atualizado com sucesso" : "Usuário criado com sucesso",
      });
    } catch (error: any) {
      console.error('Erro ao salvar usuário:', error);
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (value: string) => {
    const cleanPhone = parsePhone(value);
    setDisplayPhone(formatPhone(value));
    setFormData({...formData, phone: cleanPhone});
  };

  const handleEdit = (user: Profile) => {
    setEditingUser(user);
    setFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      secondary_email: user.secondary_email || '',
      phone: user.phone || '',
      role: user.role,
      manager_id: user.manager_id || '',
      creci: user.creci || ''
    });
    setDisplayPhone(formatPhone(user.phone || ''));
    setShowForm(true);
  };

  const handleDelete = async (user: Profile) => {
    console.log('🔍 Verificando propriedades para usuário:', user);
    console.log('🔍 User ID:', user.user_id);
    
    // Se o usuário não tem user_id, pular verificação de propriedades
    if (!user.user_id) {
      console.log('⚠️ Usuário sem user_id, pulando verificação de propriedades');
      
      if (!confirm(`Tem certeza que deseja excluir o perfil ${user.first_name} ${user.last_name}?`)) {
        return;
      }

      await deleteUserDirectly(user);
      return;
    }
    
    // Primeiro verificar se o usuário tem imóveis captados
    try {
      const { data: properties, error } = await supabase
        .from('properties')
        .select('id, listing_id, title')
        .or(`user_id.eq.${user.user_id},responsible_broker_id.eq.${user.user_id}`);

      console.log('🔍 Query result:', { properties, error });

      if (error) throw error;

      // Se tem imóveis, abrir modal de transferência
      if (properties && properties.length > 0) {
        console.log('✅ Usuário tem propriedades, abrindo modal de transferência');
        setUserToDelete(user);
        setShowTransferModal(true);
        return;
      }

      console.log('❌ Usuário não tem propriedades, prosseguindo com exclusão normal');
      
      // Se não tem imóveis, prosseguir com exclusão normal
      if (!confirm(`Tem certeza que deseja excluir o usuário ${user.first_name} ${user.last_name}?`)) {
        return;
      }

      await deleteUserDirectly(user);
    } catch (error) {
      console.error('❌ Erro ao verificar imóveis do usuário:', error);
      toast({
        title: "Erro",
        description: "Erro ao verificar dados do usuário",
        variant: "destructive",
      });
    }
  };

  const deleteUserDirectly = async (user: Profile) => {
    try {
      console.log('🗑️ Iniciando exclusão do usuário:', user.first_name, user.last_name);
      console.log('🔍 User ID para limpeza:', user.user_id);
      console.log('🔍 Profile ID para limpeza:', user.id);

      // Se não tem user_id (perfil sem usuário de auth), deletar perfil diretamente
      if (!user.user_id) {
        const { error: delError } = await supabase
          .from('profiles')
          .delete()
          .eq('id', user.id);

        if (delError) {
          console.error('❌ Erro ao excluir perfil sem user_id:', delError);
          throw delError;
        }

        console.log('✅ Perfil sem user_id excluído com sucesso');
        await loadUsers();
        toast({
          title: 'Usuário excluído',
          description: 'Perfil removido com sucesso',
        });
        return;
      }
      
      // Usar função do banco que tem privilégios elevados para deletar tudo
      const { error } = await supabase.rpc('delete_user_and_dependencies', {
        target_user_id: user.user_id,
        target_profile_id: user.id
      });

      if (error) {
        console.error('❌ Erro na função de exclusão:', error);
        throw error;
      }

      console.log('✅ Usuário e dependências excluídos com sucesso via função');
      await loadUsers();
      toast({
        title: 'Usuário excluído',
        description: 'Usuário removido com sucesso',
      });
    } catch (error: any) {
      console.error('❌ Erro ao excluir usuário:', error);
      toast({
        title: 'Erro',
        description: `Erro ao excluir usuário: ${error.message}`,
        variant: 'destructive',
      });
    }
  };

  const toggleUserStatus = async (user: Profile) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: !user.is_active })
        .eq('id', user.id);

      if (error) throw error;
      await loadUsers();
      toast({
        title: "Status alterado",
        description: `Usuário ${user.is_active ? 'desativado' : 'ativado'} com sucesso`,
      });
    } catch (error: any) {
      console.error('Erro ao alterar status:', error);
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const resetPassword = async (user: Profile) => {
    if (!confirm(`Tem certeza que deseja resetar a senha de ${user.first_name} ${user.last_name} para "Fox1234!"?`)) {
      return;
    }

    try {
      // Chamar edge function para resetar senha
      const { data, error } = await supabase.functions.invoke('reset-user-password', {
        body: {
          userId: user.user_id,
          newPassword: 'Fox1234!'
        }
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      // Atualizar flag para forçar mudança de senha
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ force_password_change: true })
        .eq('id', user.id);

      if (updateError) throw updateError;

      toast({
        title: "Senha resetada!",
        description: "Senha alterada para Fox1234! - Usuário deve trocar no próximo login",
      });
      await loadUsers();
    } catch (error: any) {
      console.error('Erro ao resetar senha:', error);
      toast({
        title: "Erro",
        description: `Erro ao resetar senha: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const confirmUserEmail = async (user: Profile) => {
    try {
      const { data, error } = await supabase.functions.invoke('confirm-user-email', {
        body: { 
          userId: user.user_id
        }
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      toast({
        title: "Email confirmado!",
        description: `Email do usuário ${user.first_name} foi confirmado com sucesso.`,
      });
      await loadUsers();
    } catch (error: any) {
      console.error('Erro ao confirmar email:', error);
      toast({
        title: "Erro",
        description: `Erro ao confirmar email: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = selectedRole === '' || user.role === selectedRole;
    
    return matchesSearch && matchesRole;
  }).sort((a, b) => {
    const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
    const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const getRoleLabel = (role: string) => {
    const labels = {
      corretor: 'Corretor',
      gerente: 'Gerente',
      rh: 'RH',
      diretor: 'Diretor'
    };
    return labels[role as keyof typeof labels] || role;
  };

  const getRoleColor = (role: string) => {
    const colors = {
      corretor: 'bg-blue-100 text-blue-800',
      gerente: 'bg-green-100 text-green-800',
      rh: 'bg-purple-100 text-purple-800',
      diretor: 'bg-red-100 text-red-800'
    };
    return colors[role as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Bulk Import Section */}
      {showBulkImport && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Cadastro em Lote</h3>
            <Button 
              onClick={() => setShowBulkImport(false)}
              variant="outline"
            >
              Voltar
            </Button>
          </div>
          <BulkUserImport />
        </div>
      )}

      {!showBulkImport && (
        <>
          {/* Header responsivo */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-6">
            <div className="flex flex-col gap-3 sm:gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Gerenciar Usuários</h1>
                <p className="text-gray-600 text-sm sm:text-base lg:text-lg">Cadastre e gerencie usuários do sistema</p>
              </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button 
                onClick={() => setShowBulkImport(true)}
                variant="outline"
                className="flex items-center justify-center gap-2 text-sm"
              >
                <Upload className="h-4 w-4" />
                <span className="hidden sm:inline">Importar em Lote</span>
                <span className="sm:hidden">Importar</span>
              </Button>
              <Button 
                onClick={() => setShowForm(true)}
                className="flex items-center justify-center gap-2 text-sm"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Novo Usuário</span>
                <span className="sm:hidden">Novo</span>
              </Button>
              </div>
            </div>
          </div>

      {/* Filters responsivos */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          />
        </div>
        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
        >
          <option value="">Todos os cargos</option>
          <option value="diretor">Diretor</option>
          <option value="gerente">Gerente</option>
          <option value="rh">RH</option>
          <option value="corretor">Corretor</option>
        </select>
      </div>

      {/* Users List responsiva */}
      <div className="bg-white rounded-lg shadow">
        {/* Versão mobile - Cards */}
        <div className="block sm:hidden space-y-3 p-4">
          {filteredUsers.map((user) => (
            <div key={user.id} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {user.first_name} {user.last_name}
                  </div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                  {user.phone && (
                    <div className="text-xs text-gray-500">{user.phone}</div>
                  )}
                </div>
                <div className="flex flex-col gap-1 ml-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                    {getRoleLabel(user.role)}
                  </span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                  {user.force_password_change && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Trocar senha
                    </span>
                  )}
                </div>
              </div>
              
              {user.manager && (
                <div className="text-xs text-gray-500 mb-2">
                  Gerente: {user.manager.first_name} {user.manager.last_name}
                </div>
              )}
              
              <div className="flex gap-2 pt-2 border-t border-gray-100">
                <button
                  onClick={() => handleEdit(user)}
                  className="text-blue-600 hover:text-blue-900 p-1"
                  title="Editar"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => resetPassword(user)}
                  className="text-orange-600 hover:text-orange-900 p-1"
                  title="Resetar Senha"
                >
                  <Key className="h-4 w-4" />
                </button>
                <button
                  onClick={() => confirmUserEmail(user)}
                  className="text-green-600 hover:text-green-900 p-1"
                  title="Confirmar Email"
                >
                  <Mail className="h-4 w-4" />
                </button>
                <button
                  onClick={() => toggleUserStatus(user)}
                  className={`p-1 ${user.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                  title={user.is_active ? 'Desativar' : 'Ativar'}
                >
                  <UserCheck className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(user)}
                  className="text-red-600 hover:text-red-900 p-1"
                  title="Excluir"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Versão desktop - Tabela */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cargo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gerente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                      {user.phone && (
                        <div className="text-sm text-gray-500">{user.phone}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.manager ? 
                      `${user.manager.first_name} ${user.manager.last_name}` : 
                      user.role === 'corretor' ? 'Sem gerente' : '-'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                      {user.force_password_change && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Trocar senha
                        </span>
                      )}
                    </div>
                  </td>
                   <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                     <div className="flex gap-2">
                       <button
                         onClick={() => handleEdit(user)}
                         className="text-blue-600 hover:text-blue-900"
                         title="Editar"
                       >
                         <Edit2 className="h-4 w-4" />
                       </button>
                       <button
                         onClick={() => resetPassword(user)}
                         className="text-orange-600 hover:text-orange-900"
                         title="Resetar Senha"
                       >
                         <Key className="h-4 w-4" />
                       </button>
                       <button
                         onClick={() => confirmUserEmail(user)}
                         className="text-green-600 hover:text-green-900"
                         title="Confirmar Email"
                       >
                         <Mail className="h-4 w-4" />
                       </button>
                       <button
                         onClick={() => toggleUserStatus(user)}
                         className={`${user.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                         title={user.is_active ? 'Desativar' : 'Ativar'}
                       >
                         <UserCheck className="h-4 w-4" />
                       </button>
                       <button
                         onClick={() => handleDelete(user)}
                         className="text-red-600 hover:text-red-900"
                         title="Excluir"
                       >
                         <Trash2 className="h-4 w-4" />
                       </button>
                     </div>
                   </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-8">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum usuário encontrado</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedRole ? 'Tente ajustar os filtros' : 'Comece criando um novo usuário'}
            </p>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">
              {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome</label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Sobrenome</label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  E-mail secundário <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <input
                  type="email"
                  value={formData.secondary_email}
                  onChange={(e) => setFormData({...formData, secondary_email: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="email.alternativo@exemplo.com"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Também receberá as notificações de novos leads atribuídos a este usuário.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Telefone</label>
                <input
                  type="tel"
                  value={displayPhone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="(11) 99999-9999"
                />
              </div>

              {['corretor', 'gerente', 'diretor'].includes(formData.role) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">CRECI</label>
                  <input
                    type="text"
                    value={formData.creci}
                    onChange={(e) => setFormData({...formData, creci: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Ex: CRECI/SP 123456"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Cargo</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value as Profile['role'], manager_id: ''})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="corretor">Corretor</option>
                  <option value="gerente">Gerente</option>
                  <option value="rh">RH</option>
                  <option value="diretor">Diretor</option>
                </select>
              </div>

              {formData.role === 'corretor' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Gerente</label>
                  <select
                    value={formData.manager_id}
                    onChange={(e) => setFormData({...formData, manager_id: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    <option value="">Selecione um gerente</option>
                    {managers.map(manager => (
                      <option key={manager.id} value={manager.id}>
                        {manager.first_name} {manager.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {!editingUser && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-sm text-blue-800">
                    <strong>🎉 Email automático:</strong> Um email de boas-vindas será enviado automaticamente para o novo usuário com:<br/>
                    • Credenciais de acesso<br/>
                    • Senha temporária: Fox1234!<br/>
                    • Instruções de primeiro acesso<br/>
                    • Informações sobre o cargo
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? 'Salvando...' : (editingUser ? 'Atualizar' : 'Criar Usuário')}
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingUser(null);
                    setDisplayPhone('');
                    setFormData({
                      first_name: '',
                      last_name: '',
                      email: '',
                      secondary_email: '',
                      phone: '',
                      role: 'corretor',
                      manager_id: '',
                      creci: ''
                    });
                  }}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Transferência de Imóveis */}
      {showTransferModal && userToDelete && (
        <TransferPropertiesModal
          isOpen={showTransferModal}
          onClose={() => {
            setShowTransferModal(false);
            setUserToDelete(null);
          }}
          userToDelete={{
            user_id: userToDelete.user_id || '',
            first_name: userToDelete.first_name,
            last_name: userToDelete.last_name,
            email: userToDelete.email
          }}
          onConfirmDelete={() => {
            if (userToDelete) {
              deleteUserDirectly(userToDelete);
            }
          }}
        />
      )}
        </>
      )}
    </div>
  );
};